import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { authPostRequest } from "../../api/authClient";
import { useAuthMe } from "../../hooks/useAuthMe";
import { SenrysaLogo } from "../../Components/SenrysaLogo";

function isValidEmail(val: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
}

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const me = useAuthMe({ redirectOn401: false });

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (me.data && !me.isError) {
      navigate("/", { replace: true });
    }
  }, [me.data, me.isError, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !isValidEmail(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);
    try {
      await authPostRequest("/member/auth/forgot-password", { email: trimmed });
      toast.success("If an account exists for that email, you will receive a reset link shortly.");
      setEmail("");
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as { message?: string })?.message;
      toast.error(typeof msg === "string" && msg.length > 0 ? msg : "Could not send reset email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <header className="h-16 flex items-center justify-between px-6 border-b border-slate-200/60 bg-white/70 backdrop-blur dark:border-slate-800/70 dark:bg-slate-900/40">
        <div className="flex items-center gap-3">
          <SenrysaLogo className="p-1" />
        </div>
      </header>

      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl shadow-emerald-900/5 dark:shadow-slate-900/50 border border-slate-200/60 dark:border-slate-700/60 p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Forgot password</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Enter your account email and we will send you a reset link if it exists.
              </p>
            </div>

            <form className="w-full space-y-4" onSubmit={onSubmit}>
              <div>
                <label htmlFor="forgot-email" className="text-sm font-medium mb-1 block text-gray-700 dark:text-slate-300">
                  Email
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full h-10 px-3 rounded-lg border-2 bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-slate-100 focus:border-emerald-600 dark:focus:border-emerald-500 focus:ring-2 focus:ring-emerald-600 dark:focus:ring-emerald-500 focus:ring-opacity-50 focus:outline-none placeholder-gray-400 dark:placeholder-slate-500"
                  placeholder="Enter your email"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full p-3 rounded-lg text-white font-medium transition-all duration-200 bg-emerald-600 hover:bg-emerald-700 dark:bg-blue-600 dark:hover:bg-blue-500 focus:ring-2 focus:ring-emerald-500 dark:focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-800 disabled:opacity-70"
              >
                {isLoading ? "Sending…" : "Send reset link"}
              </button>
            </form>

            <p className="text-center text-sm text-gray-600 dark:text-slate-400 mt-6">
              <Link to="/login" className="text-emerald-600 dark:text-blue-400 hover:text-emerald-700 dark:hover:text-blue-300 font-medium">
                Back to sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
